import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, CreditCard, RotateCcw, ScrollText } from "lucide-react";
import { MARKETPLACE, CANCELLATION_TIERS, formatUSD } from "@/lib/marketplaceTerms";
import AcceptanceLog from "@/components/AcceptanceLog";
import ReportDialog from "@/components/ReportDialog";
import AcceptAgreementDialog, { AgreementPanel } from "@/components/BookingAgreement";
import { ProposeChangeDialog, ReviewChangeOrderDialog, ChangeOrderHistory } from "@/components/ChangeOrders";
import ServiceStatusPanel from "@/components/ServiceStatusPanel";
import { PaymentProgressCard } from "@/components/PaymentProgressCard";
import { OfflinePaymentDialog } from "@/components/OfflinePaymentDialog";
import { PaymentProcessingNotice } from "@/components/PaymentProcessingNotice";

const statusColors = {
  requested: "bg-[#F5F2EB] text-[#5C5C5C]",
  quoted: "bg-[#EFEADF] text-[#1A1A1A]",
  accepted: "bg-[#8A9A86] text-white",
  paid: "bg-[#1A1A1A] text-white",
  declined: "bg-[#C05A46]/10 text-[#C05A46]",
  cancelled: "bg-[#F5F2EB] text-[#5C5C5C]",
};

const refundStatusCopy = {
  review_required: { label: "Cancellation under review", tone: "text-[#D9583A]" },
  partially_refunded: { label: "Partially refunded", tone: "text-[#D9583A]" },
  refunded: { label: "Fully refunded", tone: "text-[#D9583A]" },
  no_refund_due: { label: "No refund due", tone: "text-[#5C5C5C]" },
};

const durationLabel = (d) => (d === "1" ? "1 hour" : `${d} hours`);
const CANCEL_SUCCESS_MSG = "Your booking has been cancelled. Any eligible refund is being reviewed.";

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [msg, setMsg] = useState("");
  const [quote, setQuote] = useState(0);
  const [status, setStatus] = useState("requested");
  const [tip, setTip] = useState(0);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelledNotice, setCancelledNotice] = useState(false);
  const [summary, setSummary] = useState(null);
  const [customMode, setCustomMode] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const scrollRef = useRef(null);

  const [pendingCO, setPendingCO] = useState(null);
  const [coRefreshKey, setCoRefreshKey] = useState(0);

  const load = () => api.get(`/bookings/${id}`).then(({ data }) => { setB(data); setQuote(data.quote_amount); setStatus(data.status === "paid" ? "" : data.status); });
  const loadPending = () => {
    setCoRefreshKey(k => k + 1);
    return api.get(`/bookings/${id}/change-orders`)
      .then(({ data }) => setPendingCO((data || []).find(c => c.status === "sent") || null))
      .catch(() => setPendingCO(null));
  };
  useEffect(() => { load(); loadPending(); }, [id]);
  const loadSummary = () => api.get(`/bookings/${id}/payment-summary`).then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  useEffect(() => { loadSummary(); }, [id, b?.status, b?.refund_status]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [b?.messages?.length]);

  if (!b) return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">Loading…</div>;

  const send = async () => {
    if (!msg.trim()) return;
    try {
      await api.post("/messages", { booking_id: id, body: msg });
      setMsg(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // Vendor completion: one path for the dropdown and the button (same as "Mark Service Completed").
  const markCompleted = async () => {
    try {
      await api.post(`/bookings/${id}/service-completion`, { note: "", evidence_ref: null });
      toast.success("Marked completed — waiting for the planner to confirm");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not complete"); }
  };
  const updateBooking = async () => {
    if (!status) return;
    if (status === "completed" && b.status !== "completed") return markCompleted();
    try {
      await api.put(`/bookings/${id}`, { quote_amount: +quote, status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pay = async () => {
    if (!policyAccepted) return toast.error("Please accept the vendor's policy first");
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: id,
        origin_url: window.location.origin,
        tip_amount: Math.max(0, Number(tip) || 0),
        policy_accepted: true,
      });
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const payBalance = async (amount) => {
    try {
      const body = { booking_id: id, origin_url: window.location.origin, tip_amount: Math.max(0, Number(tip) || 0) };
      if (amount != null) body.amount = amount;
      const { data } = await api.post(`/bookings/${id}/balance-checkout`, body);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start balance checkout"); }
  };
  const payCustom = () => {
    const amt = Math.round(Number(customAmount) * 100) / 100;
    const remaining = Number(summary?.remaining) || 0;
    const minimum = Number(summary?.min_installment) || Math.min(50, remaining);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter an amount to pay.");
    if (amt > remaining + 0.001) return toast.error(`Amount can't exceed the remaining balance of ${formatUSD(remaining)}.`);
    if (amt < minimum - 0.001) return toast.error(`Minimum payment is ${formatUSD(minimum)}.`);
    payBalance(amt);
  };

  // Planner cancellation: server preview → confirm → PUT exactly {status:"cancelled"}.
  // No refund is issued here; the admin resolves any eligible refund afterwards.
  const openCancel = async () => {
    setCancelLoading(true);
    try {
      const { data } = await api.get(`/bookings/${id}/cancellation-preview`);
      setCancelPreview(data);
      setCancelOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load cancellation details");
    } finally {
      setCancelLoading(false);
    }
  };
  const confirmCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await api.put(`/bookings/${id}`, { status: "cancelled" });
      setCancelOpen(false);
      setCancelledNotice(true);
      toast.success(CANCEL_SUCCESS_MSG);
      await Promise.all([load(), loadSummary()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel booking");
    } finally {
      setCancelling(false);
    }
  };

  const isPlanner = user?.user_id === b.planner_id;
  const isVendorOwner = user?.user_id === b.vendor_owner_id;
  // Server-authoritative financial/refund state (payment-summary is the source of truth).
  const refundStatus = summary?.refund_status ?? b.refund_status ?? "none";
  const hasRefundState = refundStatus !== "none";
  const depositCollected = Number(summary?.deposit_paid) > 0; // ledger, not lifecycle status
  const isCancelled = b.status === "cancelled";
  const paymentsFrozen = isCancelled || hasRefundState || !!summary?.balance_blocked;
  const payoutHeld = paymentsFrozen || ["held_refund_review", "blocked_refunded", "held"].includes(summary?.payout_status ?? b.payout_status);
  const canPlannerCancel = isPlanner && !["cancelled", "declined", "completed"].includes(b.status);
  const daysUntil = Math.max(0, Math.ceil((new Date(b.date) - new Date()) / (1000 * 60 * 60 * 24)));
  const currentTier = CANCELLATION_TIERS.find(t => daysUntil >= t.min_days) || CANCELLATION_TIERS[CANCELLATION_TIERS.length - 1];

  // Policy snapshot is for labels only. Every dollar figure (deposit, the balance it leaves, remaining,
  // fee split) is server-priced in payment-summary — the client never does quote − deposit arithmetic.
  const snapshot = b.payment_policy_snapshot || null;
  const policyResolved = snapshot?.computed || null;
  const pricingReady = !!summary;
  const depositAmt = Number(summary?.deposit_due) || 0;
  const balanceAmt = Number(summary?.balance_after_deposit) || 0;
  // Ledger-driven remaining once a deposit is collected (installments reduce it); server policy figure before.
  const ledgerRemaining = depositCollected ? Math.max(0, Number(summary?.remaining) || 0) : balanceAmt;
  const platformFee = Number(summary?.deposit_split?.platform_fee) || 0;
  const vendorNet = Number(summary?.deposit_split?.vendor_payout) || 0;
  const balanceDays = policyResolved?.balance_due_days ?? 7;
  const balanceDueLabel = balanceDays === 0 ? "Due upon arrival" : `Due ${balanceDays} day${balanceDays === 1 ? "" : "s"} before event`;
  // Planner-facing balance due date: event date minus balanceDays days, rendered as "September 16, 2026".
  const plannerBalanceDueDate = (() => {
    if (!b.date) return "";
    try {
      const d = new Date(b.date);
      if (isNaN(d)) return "";
      d.setDate(d.getDate() - balanceDays);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
  })();

  return (
    <div data-testid="booking-detail-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-12">
      <Link to={isPlanner ? "/planner/dashboard" : "/vendor/dashboard"} className="text-sm link-underline text-[#5C5C5C]">← Back</Link>

      <div className="mt-6 sm:mt-8 grid lg:grid-cols-3 gap-6 lg:gap-10">
        <div className="lg:col-span-2 card-elegant p-0 overflow-hidden flex flex-col h-[65vh] lg:h-[70vh]">
          <div className="p-4 sm:p-6 border-b border-[#E8E2D9] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="eyebrow">Conversation</div>
              <div className="font-serif text-lg sm:text-2xl truncate">{b.vendor_name} × {b.planner_name}</div>
            </div>
            <div data-testid="booking-status-pill" className={`text-[10px] sm:text-xs uppercase tracking-widest px-2 sm:px-3 py-1 rounded-full shrink-0 ${statusColors[b.status]}`}>
              {b.status === "paid" && summary && !hasRefundState
                ? (summary.needs_reconciliation ? "Payment under review" : summary.fully_paid ? "Paid in full" : summary.remaining > 0 ? "Deposit paid" : b.status)
                : b.status}
            </div>
          </div>
          <div ref={scrollRef} data-testid="messages-list" className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 sm:space-y-4 bg-[#FDFBF7]">
            {b.messages?.map((m) => {
              const mine = m.sender_id === user?.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] sm:max-w-md p-3 sm:p-4 rounded-md ${mine ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#E8E2D9]"}`}>
                    <div className={`text-[10px] uppercase tracking-widest mb-1 ${mine ? "text-white/60" : "text-[#5C5C5C]"}`}>{m.sender_name}</div>
                    <div className="text-sm leading-relaxed">{m.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-4 border-t border-[#E8E2D9] flex gap-2">
            <Input data-testid="message-input" value={msg} onChange={e => setMsg(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()} placeholder="Write a message…" className="bg-white h-11" />
            <Button data-testid="send-message-btn" onClick={send} className="btn-sage rounded-full h-11 px-5">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="card-elegant p-6">
            <div className="eyebrow">Event date</div>
            <div className="font-serif text-2xl">{new Date(b.date).toLocaleDateString()}</div>
            <div className="text-sm text-[#5C5C5C] mt-1">{b.guest_count} guests</div>
            {b.duration_hours && (
              <div className="text-sm text-[#5C5C5C] mt-1" data-testid="booking-duration">Duration: {durationLabel(b.duration_hours)}</div>
            )}
          </div>
          {(b.booking_type ?? "quote") === "instant" && b.offering_snapshot?.title && (() => {
            const s = b.offering_snapshot;
            const unit = { per_person: "per person", per_hour: "per hour", per_item: "per item", flat_rate: "flat rate" }[s.pricing_type] || s.price_unit || "";
            const qtyLabel = { per_person: "guests", per_hour: "hours", per_item: "items" }[s.pricing_type] || "";
            const isFlat = s.pricing_type === "flat_rate";
            return (
              <div className="card-elegant p-6" data-testid="what-you-booked">
                <div className="eyebrow">What you booked</div>
                <div className="font-serif text-2xl mt-2" data-testid="booked-item-title">{s.title}</div>
                <div className="text-sm text-[#5C5C5C] mt-1" data-testid="booked-item-pricing">
                  {formatUSD(s.unit_price)} {unit}{!isFlat && Number(s.quantity) > 0 ? ` × ${s.quantity} ${qtyLabel}`.trimEnd() : ""}
                </div>
                <div className="text-xl font-semibold mt-3" data-testid="booked-item-total">{formatUSD(b.quote_amount)}</div>
              </div>
            );
          })()}
          {(isPlanner || isVendorOwner || user?.role === "admin") && <PaymentProgressCard summary={summary} />}
          <div className="card-elegant p-6">
            <div className="eyebrow">Quote total</div>
            <div className="text-4xl font-semibold mt-2" data-testid="quote-total">{formatUSD(b.quote_amount)}</div>
            {isVendorOwner && (
              <div className="mt-4 space-y-2">
                {b.agreement_id ? (
                  <div className="text-[11px] text-[color:var(--text-muted)] rounded-md bg-[#F5F2EB] border border-[#E8E2D9] p-2" data-testid="quote-locked-notice">
                    Locked by signed agreement. Cancel or complete only — quote changes require a change order.
                  </div>
                ) : (b.booking_type ?? "quote") === "instant" && b.offering_snapshot ? (
                  <div className="text-[11px] text-[color:var(--text-muted)] rounded-md bg-[#F5F2EB] border border-[#E8E2D9] p-2" data-testid="instant-price-locked-notice">
                    Instant Book price locked at booking. Decline or cancel only — the amount cannot be changed.
                  </div>
                ) : (
                  <Input data-testid="quote-input" type="number" value={quote} onChange={e => setQuote(e.target.value)} className="bg-white h-11" />
                )}
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="status-select" className="bg-white h-11"><SelectValue placeholder="Change status…" /></SelectTrigger>
                  <SelectContent>
                    {b.status === "paid" ? (
                      <>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        {!b.vendor_completion_claim && <SelectItem value="completed">Completed</SelectItem>}
                      </>
                    ) : (
                      <>
                        <SelectItem value="requested">Requested</SelectItem>
                        <SelectItem value="quoted">Quoted</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                <Button data-testid="update-quote-btn" onClick={updateBooking} disabled={!status} className="btn-sage rounded-full w-full h-11">Update</Button>
                {b.agreement_id && !pendingCO && (
                  <ProposeChangeDialog booking={b} onSubmitted={() => loadPending()} />
                )}
              </div>
            )}
            {isPlanner && b.status !== "paid" && (
              <>
                {b.status === "quoted" && (
                  <AcceptAgreementDialog
                    booking={b}
                    snapshot={snapshot}
                    policy={policyResolved}
                    defaultName={user?.name || ""}
                    onAccepted={() => load()}
                  />
                )}
                {summary?.payment_processing && !depositCollected && (
                  <PaymentProcessingNotice
                    summary={summary} isAdmin={user?.role === "admin"} onRechecked={() => { load(); loadSummary(); }}
                    message={(amt) => `Your bank is still confirming ${amt}. This usually takes a few business days — we'll email you when it settles.`}
                  />
                )}
                {(b.status === "accepted" || (b.status === "requested" && (b.booking_type ?? "quote") === "instant")) && !depositCollected && !summary?.payment_processing ? (
                  <>
                    {/* Vendor policy card — planner must review + accept before paying */}
                    {isPlanner && snapshot && (
                      <div data-testid="policy-summary-card" className="mt-4 rounded-md bg-[color:var(--surface)] border border-[color:var(--border)] p-4 space-y-2 text-xs">
                        <div className="eyebrow">Vendor's policy</div>
                        <div className="text-[13px] leading-snug"><strong>{policyResolved.deposit_summary}</strong></div>
                        <div className="text-[13px] leading-snug">{policyResolved.balance_summary}</div>
                        <div className="text-[color:var(--text-muted)] leading-snug">{policyResolved.cancellation_summary}</div>
                        {(snapshot.rescheduling_policy || snapshot.weather_policy || snapshot.late_payment_policy || snapshot.damage_policy || snapshot.other_terms) && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-[color:var(--accent)] link-underline">Additional terms</summary>
                            <div className="mt-2 space-y-1 text-[color:var(--text-muted)]">
                              {snapshot.rescheduling_policy && <div><strong className="text-[color:var(--text)]">Rescheduling:</strong> {snapshot.rescheduling_policy}</div>}
                              {snapshot.weather_policy && <div><strong className="text-[color:var(--text)]">Weather:</strong> {snapshot.weather_policy}</div>}
                              {snapshot.late_payment_policy && <div><strong className="text-[color:var(--text)]">Late payment:</strong> {snapshot.late_payment_policy}</div>}
                              {snapshot.damage_policy && <div><strong className="text-[color:var(--text)]">Damages:</strong> {snapshot.damage_policy}</div>}
                              {snapshot.other_terms && <div><strong className="text-[color:var(--text)]">Other:</strong> {snapshot.other_terms}</div>}
                            </div>
                          </details>
                        )}
                        <label className="flex items-start gap-2 mt-3 pt-3 border-t border-[color:var(--border)] cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="policy-accept-checkbox"
                            checked={policyAccepted}
                            onChange={(e) => setPolicyAccepted(e.target.checked)}
                            className="mt-0.5 shrink-0"
                          />
                          <span className="text-[12px] leading-snug">
                            I have read and agree to this vendor's payment and cancellation policies.
                          </span>
                        </label>
                      </div>
                    )}
                    <div className="mt-4 space-y-2">
                      <label className="eyebrow block" htmlFor="tip-input">Add a tip (optional — 100% to vendor)</label>
                      <div className="flex gap-2">
                        {[0, 50, 100, 200].map(v => (
                          <button
                            key={v}
                            type="button"
                            data-testid={`tip-preset-${v}`}
                            onClick={() => setTip(v)}
                            className={`text-xs rounded-full px-3 py-1.5 border ${Number(tip) === v ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "border-[color:var(--border)] hover:border-[color:var(--border-strong)]"}`}
                          >
                            {v === 0 ? "None" : formatUSD(v)}
                          </button>
                        ))}
                        <input
                          id="tip-input"
                          data-testid="tip-custom-input"
                          type="number"
                          min="0"
                          step="1"
                          value={tip}
                          onChange={(e) => setTip(e.target.value)}
                          className="flex-1 h-8 rounded-full border border-[color:var(--border)] px-3 text-xs text-right"
                          placeholder="Custom $"
                        />
                      </div>
                    </div>
                    <div className="mt-3 p-3 rounded-md bg-[color:var(--surface)] text-xs text-[color:var(--text-muted)]">
                      Secured by Stripe. You'll be redirected to complete payment.
                    </div>
                    <Button
                      data-testid="pay-deposit-btn"
                      disabled={isPlanner && !policyAccepted}
                      className="btn-sage rounded-full w-full h-11 mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={pay}
                    >
                      <CreditCard className="w-4 h-4 mr-2" /> Pay {formatUSD(depositAmt + (Number(tip) || 0))}
                      {Number(tip) > 0 && <span className="ml-1 text-[10px] opacity-80">(deposit + tip)</span>}
                    </Button>
                  </>
                ) : null}
              </>
            )}
            {/* Ledger says the deposit was collected but the lifecycle status disagrees:
                never offer a payment button — the backend would reject it anyway. */}
            {isPlanner && depositCollected && b.status !== "paid" && !["completed", "cancelled", "declined"].includes(b.status) && !paymentsFrozen && (
              <div className="mt-4 p-3 rounded-md bg-[color:var(--surface)] border border-[color:var(--border)] text-sm" data-testid="deposit-received-notice">
                <div className="text-[color:var(--verified)] font-medium">✓ Deposit received — {formatUSD(summary.deposit_paid)} paid</div>
                {summary.remaining > 0 && <div className="mt-1">{formatUSD(summary.remaining)} remaining</div>}
                <div className="text-xs text-[color:var(--text-muted)] mt-2">
                  Balance payment is temporarily unavailable. Please contact NContro if this persists.
                </div>
              </div>
            )}
            {b.status === "paid" && !hasRefundState && !summary?.fully_paid && !summary?.needs_reconciliation && <div className="mt-4 text-sm text-[color:var(--verified)]" data-testid="paid-badge">✓ Deposit paid</div>}
            {b.status === "completed" && <div className="mt-4 text-sm text-[color:var(--verified)]" data-testid="completed-badge">✓ Booking completed{b.completed_at ? ` on ${new Date(b.completed_at).toLocaleDateString()}` : ""}</div>}
            {/* Remaining balance — planner action. Never shown once cancelled / under refund review / refunded,
                nor while a bank payment is still clearing. */}
            {(isPlanner || user?.role === "admin") && summary?.payment_processing && b.status === "paid" && !paymentsFrozen && (
              <PaymentProcessingNotice
                summary={summary} isAdmin={user?.role === "admin"} onRechecked={() => { load(); loadSummary(); }}
                message={(amt) => `${isPlanner ? "Your" : "The planner's"} bank is still confirming ${amt}. No further payment can be started until it settles.`}
              />
            )}
            {user?.role === "admin" && ["paid", "completed"].includes(b.status) && summary && !hasRefundState && summary.remaining > 0 && (
              <OfflinePaymentDialog bookingId={id} remaining={summary.remaining} onRecorded={() => { load(); loadSummary(); }} />
            )}
            {isPlanner && summary && summary.remaining > 0 && b.status === "paid" && !paymentsFrozen && !summary.payment_processing && (
              <div className="mt-4 p-3 rounded-md bg-[#EFEADF] border border-[#E8E2D9]" data-testid="balance-panel">
                <div className="text-xs text-[color:var(--text-muted)]">Remaining balance owed to vendor</div>
                <div className="font-serif text-2xl mt-1" data-testid="balance-remaining">{formatUSD(summary.remaining)}</div>
                {summary.balance_paid > 0 && (
                  <div className="text-[11px] text-[color:var(--text-muted)] mt-1" data-testid="balance-paid-so-far">
                    {formatUSD(summary.balance_paid)} already paid toward the balance
                  </div>
                )}
                <Button
                  data-testid="pay-balance-btn"
                  onClick={() => payBalance()}
                  className="rounded-full h-11 mt-3 w-full bg-[#1A1A1A] hover:bg-black text-white"
                >
                  <CreditCard className="w-4 h-4 mr-2" /> Pay full balance — {formatUSD(summary.remaining)}
                </Button>
                {!customMode ? (
                  <button
                    type="button"
                    data-testid="pay-another-amount-btn"
                    onClick={() => setCustomMode(true)}
                    className="w-full text-center text-sm mt-3 link-underline text-[#1A1A1A]"
                  >
                    Pay another amount
                  </button>
                ) : (
                  <div className="mt-3 space-y-2" data-testid="custom-amount-form">
                    <label className="text-xs text-[color:var(--text-muted)]" htmlFor="custom-balance-amount">
                      Amount (min {formatUSD(summary.min_installment ?? Math.min(50, summary.remaining))}, max {formatUSD(summary.remaining)})
                    </label>
                    <div className="flex gap-2">
                      <Input
                        id="custom-balance-amount"
                        data-testid="custom-amount-input"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min={summary.min_installment ?? Math.min(50, summary.remaining)}
                        max={summary.remaining}
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="0.00"
                        className="bg-white h-11"
                      />
                      <Button data-testid="pay-custom-btn" onClick={payCustom} className="rounded-full h-11 px-5 bg-[#1A1A1A] hover:bg-black text-white">
                        Pay
                      </Button>
                    </div>
                    <button type="button" data-testid="custom-amount-cancel" onClick={() => { setCustomMode(false); setCustomAmount(""); }} className="text-xs link-underline text-[color:var(--text-muted)]">
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
            {isPlanner && summary && summary.fully_paid && b.status === "paid" && !paymentsFrozen && (
              <div className="mt-4 text-sm text-[color:var(--verified)]" data-testid="fully-paid-badge">
                ✓ Fully paid — {formatUSD(summary.net_collected)} collected
              </div>
            )}
            {/* Mark Completed — vendor/admin action once fully paid */}
            {isVendorOwner && b.status === "paid" && summary?.fully_paid && !paymentsFrozen && !b.vendor_completion_claim && (
              <Button
                data-testid="mark-completed-btn"
                onClick={markCompleted}
                variant="outline"
                className="rounded-full w-full h-11 mt-3 border-[color:var(--verified)] text-[color:var(--verified)]"
              >
                Mark booking completed
              </Button>
            )}
            {(isPlanner && (cancelledNotice || (isCancelled && refundStatus === "review_required"))) && (
              <div className="mt-4 p-3 rounded-md bg-[#F5F2EB] border border-[#E8E2D9] text-sm" data-testid="cancelled-review-banner">
                {CANCEL_SUCCESS_MSG}
              </div>
            )}
            {hasRefundState && refundStatusCopy[refundStatus] && (
              <div className={`mt-4 text-sm ${refundStatusCopy[refundStatus].tone}`} data-testid={`refund-status-${refundStatus}`}>
                ↻ {refundStatusCopy[refundStatus].label}
                {Number(summary?.refunded ?? b.amount_refunded) > 0 && ` — ${formatUSD(summary?.refunded ?? b.amount_refunded)} refunded`}
                {b.last_refund_at && ` on ${new Date(b.last_refund_at).toLocaleDateString()}`}
              </div>
            )}
            {canPlannerCancel && (
              <Button
                data-testid="cancel-booking-btn"
                variant="outline"
                onClick={openCancel}
                disabled={cancelLoading}
                className="rounded-full w-full h-11 mt-4 border-[#D9583A] text-[#D9583A] hover:bg-[#D9583A]/10"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {cancelLoading ? "Loading…" : "Cancel booking"}
              </Button>
            )}
          </div>

          {/* Payout waterfall — plain-English breakdown, driven by the vendor's own policy.
              Planner view is intentionally trimmed: it must NOT expose platform commission
              or vendor net proceeds. Vendors continue to see the full breakdown.

              For QUOTE-REQUIRED bookings in "requested" status, we hide the payment breakdown
              entirely — the planner is waiting for the vendor's formal quote and NO payment
              is due yet. The card becomes a status card with an optional estimated cost. */}
          {isPlanner && (b.booking_type ?? "quote") === "quote" && b.status === "requested" ? (
            <div data-testid="quote-request-status" className="card-elegant p-6">
              <div className="eyebrow mb-4">Quote request</div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Status</span>
                  <span className="font-semibold" data-testid="quote-request-status-label">Waiting for vendor quote</span>
                </div>
                {b.date && (
                  <div className="flex items-center gap-3">
                    <span className="flex-1 text-[color:var(--text-muted)]">Event date</span>
                    <span className="font-semibold">{new Date(b.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
                  </div>
                )}
                {Number(b.guest_count) > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="flex-1 text-[color:var(--text-muted)]">Guests</span>
                    <span className="font-semibold">{b.guest_count}</span>
                  </div>
                )}
                {b.duration_hours && (
                  <div className="flex items-center gap-3">
                    <span className="flex-1 text-[color:var(--text-muted)]">Duration</span>
                    <span className="font-semibold" data-testid="quote-request-duration">{durationLabel(b.duration_hours)}</span>
                  </div>
                )}
                {Number(b.quote_amount) > 0 && (
                  <div className="flex items-center gap-3 border-t border-[color:var(--border)] pt-3">
                    <span className="flex-1 text-[color:var(--text-muted)]">Estimated cost</span>
                    <span className="font-semibold whitespace-nowrap" data-testid="quote-request-estimate">{formatUSD(b.quote_amount)}</span>
                  </div>
                )}
              </div>
              {Array.isArray(b.selected_items) && b.selected_items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[color:var(--border)]">
                  <div className="eyebrow mb-2">Interested items ({b.selected_items.length})</div>
                  <ul className="text-[13px] space-y-1 text-[color:var(--text-muted)]" data-testid="booking-selected-items">
                    {b.selected_items.map((id) => <li key={id} className="font-mono text-[11px]">· {id}</li>)}
                  </ul>
                  <p className="text-[11px] text-[color:var(--text-muted)] italic mt-2">Reference only — no listed pricing is binding until you send the final quote.</p>
                </div>
              )}
              <p className="mt-4 text-[12px] text-[color:var(--text-muted)] leading-relaxed">
                This estimate is based on the vendor's listed pricing. The vendor will review your request and send a final quote. <strong className="text-neutral-900 font-semibold">No payment is due until you accept the quote.</strong>
              </p>
            </div>
          ) : (
          <div data-testid="payment-breakdown" className="card-elegant p-6">
            <div className="eyebrow mb-4">{paymentsFrozen ? "Payment status" : isPlanner ? "Payment summary" : "How your payout is calculated"}</div>
            {paymentsFrozen ? (
              /* Cancelled / under review / refunded — server totals only, no payable balance, no active-earning wording. */
              <ol className="space-y-3 text-sm" data-testid="frozen-payment-summary">
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Status</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="frozen-status-label">
                    {summary?.needs_reconciliation ? "Payment records under review" : refundStatusCopy[refundStatus]?.label || (isCancelled ? "Cancelled" : b.status)}
                  </span>
                </li>
                {summary?.needs_reconciliation && (
                  <li className="text-xs text-[color:var(--text-muted)] pb-1" data-testid="reconciliation-note">
                    NContro is reconciling the payment records for this booking. No payment is due right now — we'll let you know once it's resolved.
                  </li>
                )}
                <li className="flex items-center gap-3">
                  <span className="flex-1">Collected</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="frozen-collected">{formatUSD(summary?.collected ?? 0)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1">Refunded</span>
                  <span className="font-semibold whitespace-nowrap text-[#D9583A]" data-testid="frozen-refunded">{formatUSD(summary?.refunded ?? 0)}</span>
                </li>
                <li className="flex items-center gap-3 border-t border-[color:var(--border)] pt-3">
                  <span className="flex-1">{refundStatus === "review_required" ? "Held pending review" : "Net collected"}</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="frozen-net">{formatUSD(summary?.net_collected ?? 0)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Remaining balance</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="frozen-remaining">{formatUSD(summary?.remaining ?? 0)}</span>
                </li>
                {isVendorOwner && payoutHeld && (
                  <li className="text-xs text-[color:var(--text-muted)] pt-2" data-testid="payout-held-note">
                    {refundStatus === "refunded" ? "Payout blocked — this booking was fully refunded." : refundStatus === "no_refund_due" ? "Refund review closed — no refund due." : "Payout on hold until an admin resolves the refund review."}
                  </li>
                )}
              </ol>
            ) : isPlanner && summary?.fully_paid ? (
              <ol className="space-y-3 text-sm" data-testid="payment-summary-paid">
                <li className="flex items-center gap-3">
                  <span className="flex-1">Booking total</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="planner-total">{formatUSD(b.quote_amount)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Deposit paid</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="paid-deposit">{formatUSD(summary.deposit_paid)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Final balance paid</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="paid-balance">{formatUSD(summary.balance_paid)}</span>
                </li>
                {summary.tips_collected > 0 && (
                  <li className="flex items-center gap-3">
                    <span className="flex-1 text-[color:var(--text-muted)]">Tips</span>
                    <span className="font-semibold whitespace-nowrap text-[color:var(--verified)]" data-testid="paid-tips">+ {formatUSD(summary.tips_collected)}</span>
                  </li>
                )}
                <li className="flex items-center gap-3 border-t border-[color:var(--border)] pt-3">
                  <span className="flex-1">Total paid</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="paid-total">{formatUSD(summary.collected)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Balance due</span>
                  <span className="font-semibold whitespace-nowrap text-[color:var(--verified)]" data-testid="planner-remaining">{formatUSD(0)}</span>
                </li>
              </ol>
            ) : isPlanner ? (
              <ol className="space-y-3 text-sm" data-testid="payment-summary">
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">1</span>
                  <span className="flex-1">Booking total</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="planner-total">{formatUSD(b.quote_amount)}</span>
                </li>
                {!pricingReady ? null : depositAmt > 0 ? (
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">2</span>
                    <span className="flex-1">
                      {summary?.deposit_paid > 0 ? "Deposit paid" : depositAmt >= (Number(b.quote_amount) || 0) ? "Due today" : "Deposit due today"}
                      {snapshot?.deposit_type === "percentage" && depositAmt < (Number(b.quote_amount) || 0) && (
                        <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] ml-1">({snapshot.deposit_amount}%)</span>
                      )}
                    </span>
                    <span className="font-semibold whitespace-nowrap" data-testid="planner-due-today">{formatUSD(summary?.deposit_paid > 0 ? summary.deposit_paid : depositAmt)}</span>
                  </li>
                ) : (
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">2</span>
                    <span className="flex-1 text-[color:var(--text-muted)]">No deposit required by this vendor.</span>
                  </li>
                )}
                {Number(tip) > 0 && depositAmt > 0 && (
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">+</span>
                    <span className="flex-1">Your tip</span>
                    <span className="font-semibold whitespace-nowrap text-[color:var(--verified)]">+ {formatUSD(Number(tip))}</span>
                  </li>
                )}
                {summary?.balance_paid > 0 && (
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">✓</span>
                    <span className="flex-1 text-[color:var(--text-muted)]">Balance payments made</span>
                    <span className="font-semibold whitespace-nowrap" data-testid="planner-balance-paid">{formatUSD(summary.balance_paid)}</span>
                  </li>
                )}
                <li className="flex items-center gap-3 border-t border-[color:var(--border)] pt-3">
                  <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">3</span>
                  <span className="flex-1 text-[color:var(--text-muted)]">
                    Remaining balance{ledgerRemaining > 0 ? ` — due ${plannerBalanceDueDate}` : ""}
                  </span>
                  <span className="font-semibold whitespace-nowrap" data-testid="planner-remaining">{formatUSD(ledgerRemaining)}</span>
                </li>
              </ol>
            ) : summary?.net_collected > 0 ? (
              /* Vendor/admin after money moved: the same ledger as Payment progress. */
              <ol className="space-y-3 text-sm" data-testid="payout-waterfall">
                <li className="flex items-center gap-3">
                  <span className="flex-1">Booking total</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="waterfall-total">{formatUSD(b.quote_amount)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1">Collected so far</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="waterfall-collected">{formatUSD(summary.net_collected)}</span>
                </li>
                {summary.tips_collected > 0 && (
                  <li className="flex items-center gap-3">
                    <span className="flex-1 text-[color:var(--text-muted)]">Tips</span>
                    <span className="font-semibold whitespace-nowrap text-[color:var(--verified)]" data-testid="waterfall-tips">+ {formatUSD(summary.tips_collected)}</span>
                  </li>
                )}
                <li className="flex items-center gap-3">
                  <span className="flex-1">NContro platform fee</span>
                  <span className="font-semibold whitespace-nowrap text-[color:var(--text-muted)]" data-testid="waterfall-fee">− {formatUSD(summary.platform_fee_collected)}</span>
                </li>
                <li className="flex items-center gap-3 border-t border-[color:var(--border)] pt-3">
                  <span className="flex-1 font-semibold">Your share of payments so far</span>
                  <span className="font-semibold whitespace-nowrap text-[color:var(--verified)]" data-testid="waterfall-vendor-payout">{formatUSD(summary.vendor_share_collected)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">
                    {summary.fully_paid ? "Remaining balance" : `Remaining balance${summary.due_date ? ` — due ${new Date(`${summary.due_date}T12:00:00`).toLocaleDateString()}` : ""}`}
                  </span>
                  <span className="font-semibold whitespace-nowrap" data-testid="waterfall-remaining">{formatUSD(summary.remaining)}</span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm" data-testid="payout-waterfall">
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">1</span>
                  <span className="flex-1">Booking total</span>
                  <span className="font-semibold whitespace-nowrap">{formatUSD(b.quote_amount)}</span>
                </li>
                {!pricingReady ? null : depositAmt > 0 ? (
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">2</span>
                    <span className="flex-1">
                      Deposit due today
                      {snapshot?.deposit_type === "percentage" && (
                        <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] ml-1">({snapshot.deposit_amount}%)</span>
                      )}
                    </span>
                    <span className="font-semibold whitespace-nowrap" data-testid="waterfall-deposit">{formatUSD(depositAmt)}</span>
                  </li>
                ) : (
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">2</span>
                    <span className="flex-1 text-[color:var(--text-muted)]">No deposit required by this vendor.</span>
                  </li>
                )}
                {depositAmt > 0 && (
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">3</span>
                    <span className="flex-1">NContro platform fee ({MARKETPLACE.platform_fee_percent}% of deposit)</span>
                    <span className="font-semibold whitespace-nowrap text-[color:var(--text-muted)]">− {formatUSD(platformFee)}</span>
                  </li>
                )}
                <li className="flex items-center gap-3 border-t border-[color:var(--border)] pt-3">
                  <span className="w-6 h-6 rounded-full bg-[color:var(--verified-soft)] text-[10px] flex items-center justify-center font-semibold shrink-0 text-[color:var(--verified)]">4</span>
                  <span className="flex-1 font-semibold">Your share of this payment</span>
                  <span className="font-semibold whitespace-nowrap text-[color:var(--verified)]" data-testid="waterfall-vendor-payout">
                    {formatUSD(vendorNet)}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[color:var(--surface)] text-[10px] flex items-center justify-center font-semibold shrink-0">5</span>
                  <span className="flex-1 text-[color:var(--text-muted)]">Remaining balance — {balanceDueLabel.toLowerCase()}</span>
                  <span className="font-semibold whitespace-nowrap">{formatUSD(balanceAmt)}</span>
                </li>
              </ol>
            )}
            <div className="mt-5 pt-4 border-t border-[color:var(--border)]">
              <div className="eyebrow mb-2">Cancellation policy</div>
              <div className="text-xs text-[color:var(--text-muted)] leading-relaxed" data-testid="cancellation-summary">
                {policyResolved?.cancellation_summary || `${daysUntil} days until event — ${isPlanner ? `${currentTier.refund_percent}% deposit refund` : `you keep ${100 - currentTier.refund_percent}% of the deposit`} if cancelled today.`}
              </div>
              {snapshot?.is_custom === false && (
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-2">Platform default policy</div>
              )}
            </div>
          </div>
          )}
        </aside>
      </div>

      {/* Legal acceptance log for this booking — always visible to planner, vendor, admin. */}
      <div className="mt-6 sm:mt-8 space-y-4">
        <AgreementPanel bookingId={id} />
        <ServiceStatusPanel bookingId={id} user={user} booking={b} />
        <ChangeOrderHistory bookingId={id} refreshKey={coRefreshKey} />
        {pendingCO && isPlanner && (
          <div className="card-elegant p-5 border-l-4 border-[color:var(--accent)]" data-testid="pending-co-banner">
            <div className="eyebrow mb-1">Vendor proposed a change</div>
            <p className="text-sm text-[#5C5C5C] mb-3">
              Review the requested update to your booking. Your current signed agreement stays in place until you accept a new one.
            </p>
            <ReviewChangeOrderDialog changeOrder={pendingCO} defaultName={user?.name || ""} onDone={() => { load(); loadPending(); }} />
          </div>
        )}
        {pendingCO && isVendorOwner && (
          <div className="card-elegant p-5" data-testid="pending-co-vendor-notice">
            <div className="eyebrow mb-1">Change order pending planner review</div>
            <p className="text-sm text-[#5C5C5C] mb-3">Reason: {pendingCO.reason}</p>
            <Button
              data-testid="withdraw-co-btn"
              variant="outline"
              className="rounded-full"
              onClick={async () => {
                await api.post(`/change-orders/${pendingCO.change_order_id}/withdraw`);
                toast.success("Change order withdrawn");
                loadPending();
              }}
            >Withdraw proposal</Button>
          </div>
        )}
        <details className="card-elegant p-5" data-testid="booking-legal-log">
          <summary className="cursor-pointer inline-flex items-center gap-2 text-sm">
            <ScrollText className="w-4 h-4 text-[color:var(--text-muted)]" />
            <span className="eyebrow">Legal acceptance log for this booking</span>
          </summary>
          <div className="mt-4">
            <AcceptanceLog bookingId={id} />
          </div>
        </details>
        {(isPlanner || isVendorOwner) && (
          <div className="flex justify-end" data-testid="booking-report-row">
            {isPlanner ? (
              <ReportDialog
                targetType="vendor"
                targetId={b.vendor_id}
                targetLabel={b.vendor_name}
                bookingId={b.booking_id}
              />
            ) : (
              <ReportDialog
                targetType="user"
                targetId={b.planner_id}
                targetLabel={b.planner_name}
                bookingId={b.booking_id}
              />
            )}
          </div>
        )}
      </div>

      <Dialog open={cancelOpen} onOpenChange={(o) => { if (!cancelling) setCancelOpen(o); }}>
        <DialogContent data-testid="cancel-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cancel this booking?</DialogTitle>
          </DialogHeader>
          {cancelPreview && (
            <div className="space-y-4 text-sm">
              <ol className="space-y-2 rounded-md bg-white border border-[#E8E2D9] p-4" data-testid="cancel-preview">
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Amount collected</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="cancel-preview-collected">{formatUSD(cancelPreview.collected ?? 0)}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Estimated eligible refund</span>
                  <span className="font-semibold whitespace-nowrap" data-testid="cancel-preview-eligible">
                    {formatUSD(cancelPreview.remaining_eligible ?? cancelPreview.eligible_amount ?? 0)}
                    {cancelPreview.policy_calc?.refund_percent != null && (
                      <span className="ml-1 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]" data-testid="cancel-preview-percent">({cancelPreview.policy_calc.refund_percent}%)</span>
                    )}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-1 text-[color:var(--text-muted)]">Free-cancellation window</span>
                  <span className={`font-semibold whitespace-nowrap ${cancelPreview.policy_calc?.within_free_window ? "text-[color:var(--verified)]" : "text-[#D9583A]"}`} data-testid="cancel-preview-window">
                    {cancelPreview.policy_calc?.within_free_window ? "Inside window" : "Outside window"}
                  </span>
                </li>
                {cancelPreview.policy_calc && (
                  <li className="text-xs text-[color:var(--text-muted)] pt-2 border-t border-[#E8E2D9]" data-testid="cancel-preview-basis">
                    {cancelPreview.policy_calc.basis
                      || `Policy basis: full refund if cancelled at least ${Math.round((cancelPreview.policy_calc.free_until_hours ?? 0) / 24)} days before the event, otherwise ${cancelPreview.policy_calc.refund_percent_after ?? 0}% of the amount collected.`}
                    {cancelPreview.policy_calc.hours_before_event != null && ` About ${Math.max(0, Math.round(cancelPreview.policy_calc.hours_before_event / 24))} day(s) remain before your event.`}
                    {cancelPreview.policy_calc.policy_snapshot_available === false && " (Platform default policy.)"}
                  </li>
                )}
              </ol>
              <p className="text-[color:var(--text-muted)]" data-testid="cancel-warning">
                <strong className="text-[#D9583A]">This cannot be undone.</strong> The booking is cancelled immediately and the date is released. Any eligible refund is reviewed by NContro before it is issued — no refund is sent automatically.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelling} className="rounded-full">Keep booking</Button>
            <Button
              data-testid="confirm-cancel-btn"
              onClick={confirmCancel}
              disabled={cancelling || !cancelPreview?.can_cancel}
              className="rounded-full bg-[#D9583A] hover:bg-[#C04A2E] text-white disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
